import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { ArticleBranchOption } from '@texas/api/endpoints/articlesApi';
import { articleContextEvents } from '@texas/components/article/articleContext/articleContextEvents';
import { useArticleContext } from '@texas/components/article/articleContext/useArticleContext';
import { Icons } from '@texas/components/shared/Icons';
import { useActiveSession } from '@texas/hooks/useSession';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  articleId: number;
  branches: ArticleBranchOption[];
  loading: boolean;
  ready: boolean;
  variantId: number;
}

const EMPTY_BRANCH_IDS: number[] = [];

export function ArticleActivityScope({
  articleId,
  branches,
  loading,
  ready,
  variantId,
}: Props) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const context = useArticleContext();

  useEffect(() => {
    if (!ready) return;

    if (context.articleId === articleId) {
      if (context.variantId !== variantId) {
        articleContextEvents.dispatch({
          articleId,
          branchIds: context.branchIds,
          variantId,
        });
      }
      return;
    }

    const defaultBranchIds =
      currentUser.branchId &&
      branches.some((branch) => branch.id === currentUser.branchId)
        ? [currentUser.branchId]
        : [];

    articleContextEvents.dispatch({
      articleId,
      branchIds: defaultBranchIds,
      variantId,
    });
  }, [
    articleId,
    branches,
    context.articleId,
    context.branchIds,
    context.variantId,
    currentUser.branchId,
    ready,
    variantId,
  ]);

  const selectedBranchIds =
    context.articleId === articleId ? context.branchIds : EMPTY_BRANCH_IDS;

  const label = useMemo(() => {
    if (selectedBranchIds.length === 0) return t('filter.allBranches');

    if (selectedBranchIds.length === 1) {
      return (
        branches.find((item) => item.id === selectedBranchIds[0])?.identifier ??
        t('general.branch')
      );
    }

    return t('general.branchesCount', { count: selectedBranchIds.length });
  }, [branches, selectedBranchIds, t]);

  function updateBranchIds(values: string | string[]) {
    const branchIds = (Array.isArray(values) ? values : [values]).map(Number);
    articleContextEvents.dispatch({ articleId, branchIds, variantId });
  }

  return (
    <Menu closeOnSelect={false} placement="bottom-start">
      <MenuButton
        as={Box}
        bg="navigation.contextActiveBackground"
        border="1px solid"
        borderColor="navigation.contextBorder"
        cursor="pointer"
        px={2.5}
        py={2}
        w="full"
        _hover={{ bg: 'navigation.contextHoverBackground' }}
      >
        <Flex align="center" gap={2}>
          <Text
            color="navigation.contextMuted"
            flexShrink={0}
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="0.08em"
            textTransform="uppercase"
            whiteSpace="nowrap"
          >
            {t('navigation.activityFor')}
          </Text>
          <Flex align="center" flex={1} gap={1} justify="flex-end" minW={0}>
            {loading ? (
              <Spinner size="xs" />
            ) : (
              <Text fontSize="sm" fontWeight="semibold" minW={0} noOfLines={1}>
                {label}
              </Text>
            )}
            <Icons.ChevronDown boxSize={3} flexShrink={0} />
          </Flex>
        </Flex>
      </MenuButton>
      <MenuList borderRadius={0} minW="216px" py={1}>
        <MenuItemOption
          isChecked={selectedBranchIds.length === 0}
          onClick={() => updateBranchIds([])}
          value="all"
        >
          {t('filter.allBranches')}
        </MenuItemOption>
        <MenuOptionGroup
          onChange={updateBranchIds}
          type="checkbox"
          value={selectedBranchIds.map(String)}
        >
          {branches.map((branch) => (
            <MenuItemOption key={branch.id} value={String(branch.id)}>
              {branch.name} ({branch.identifier})
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
}
